import { WebGLRenderer } from 'pixi.js';
import type { ViewSystem } from 'pixi.js';
import 'pixi.js/browser';
import type { Resolution } from './types';
import './libs/clipFilterBoundsToViewport';

interface RefCountedRenderer {
    current: LuminaryRenderer | null;
    count: number;
    ready: boolean;
    initPromise: Promise<boolean> | null;
    maxOutput: { width: number; height: number };
    maxResolution: number;
}

interface LuminaryRendererManagerConfig {
    disabled: boolean;
    antialias: boolean;
    onContextLost: () => void;
    onContextRestored: () => void;
    onInitFailed: () => void;
}

export type LuminaryRenderer = WebGLRenderer & {
    /** @deprecated Use `canvas`. */
    view: ViewSystem & Pick<HTMLCanvasElement, 'addEventListener' | 'removeEventListener'>;
};

function forwardViewListenersToCanvas(renderer: WebGLRenderer) {
    const { canvas } = renderer;
    Object.assign(renderer.view, {
        addEventListener: (...args: Parameters<HTMLCanvasElement['addEventListener']>) =>
            canvas.addEventListener(...args),
        removeEventListener: (...args: Parameters<HTMLCanvasElement['removeEventListener']>) =>
            canvas.removeEventListener(...args),
    });
}

export class LuminaryRendererManager {
    static #shared: RefCountedRenderer = {
        current: null,
        count: 0,
        ready: false,
        initPromise: null,
        maxOutput: { width: 0, height: 0 },
        maxResolution: 0,
    };

    /**
     * @internal
     */
    static get _sharedRenderer(): RefCountedRenderer {
        return LuminaryRendererManager.#shared;
    }

    renderer: LuminaryRenderer | null = null;

    rendererCreatedAt: number | null = null;
    webGLContextLost = false;
    webGLContextLostAt: number | null = null;
    readonly webglContextInfo = { renderer: '', vendor: '', extensions: '' };

    #disabled: boolean;
    #antialias: boolean;
    #onContextLost: () => void;
    #onContextRestored: () => void;
    #onInitFailed: () => void;
    #removeContextListeners: (() => void) | null = null;

    constructor(config: LuminaryRendererManagerConfig) {
        this.#disabled = config.disabled;
        this.#antialias = config.antialias;
        this.#onContextLost = config.onContextLost;
        this.#onContextRestored = config.onContextRestored;
        this.#onInitFailed = config.onInitFailed;
    }

    get disabled(): boolean {
        return this.#disabled;
    }

    get isReady(): boolean {
        return LuminaryRendererManager.#shared.ready;
    }

    async ready(): Promise<void> {
        await LuminaryRendererManager.#shared.initPromise;
    }

    get resolution(): number | null {
        if (!this.renderer) {
            return null;
        }
        const shared = LuminaryRendererManager.#shared;
        return shared.ready ? this.renderer.resolution : shared.maxResolution;
    }

    lazyInit(outputResolution: Resolution, referenceResolution: Resolution) {
        if (this.renderer || this.#disabled) {
            return;
        }

        const resolution = outputResolution.height / referenceResolution.height;
        const shared = LuminaryRendererManager.#shared;

        if (shared.current === null) {
            const renderer = new WebGLRenderer();
            shared.current = renderer as LuminaryRenderer;
            shared.ready = false;
            shared.initPromise = renderer
                .init({
                    width: referenceResolution.width,
                    height: referenceResolution.height,
                    resolution,
                    antialias: this.#antialias,
                    backgroundAlpha: 0,
                    powerPreference: 'high-performance',
                    skipExtensionImports: true,
                })
                .then(
                    () => {
                        forwardViewListenersToCanvas(renderer);
                        shared.ready = true;
                        return true;
                    },
                    (err) => {
                        console.error('WebGLRenderer init failed:', err);
                        LuminaryRendererManager.#resetShared();
                        return false;
                    },
                );
        }
        shared.maxResolution = Math.max(shared.maxResolution, resolution);
        shared.count += 1;

        this.renderer = shared.current;
        this.rendererCreatedAt = performance.now();

        if (shared.ready) {
            this.#onSharedInitSettled(true);
        } else {
            void shared.initPromise!.then((initialized) => this.#onSharedInitSettled(initialized));
        }
    }

    #onSharedInitSettled(initialized: boolean) {
        if (!this.renderer) {
            return;
        }
        if (!initialized) {
            this.renderer = null;
            this.#onInitFailed();
            return;
        }
        this.#attachContextListeners();
        this.#captureWebglContextInfo();
        this.#fitCanvasToOutputs();
    }

    static #resetShared() {
        const shared = LuminaryRendererManager.#shared;
        shared.current = null;
        shared.count = 0;
        shared.ready = false;
        shared.initPromise = null;
        shared.maxOutput = { width: 0, height: 0 };
        shared.maxResolution = 0;
    }

    #fitCanvasToOutputs() {
        const shared = LuminaryRendererManager.#shared;
        const renderer = shared.current;
        if (!renderer || !shared.ready) {
            return;
        }
        if (renderer.resolution < shared.maxResolution) {
            renderer.resolution = shared.maxResolution;
        }
        const { width, height } = shared.maxOutput;
        if (renderer.canvas.width === width && renderer.canvas.height === height) {
            return;
        }
        renderer.resize(width / renderer.resolution, height / renderer.resolution);
    }

    #attachContextListeners() {
        if (!this.renderer?.canvas || this.#removeContextListeners) {
            return;
        }
        const canvas = this.renderer.canvas;
        const handleContextLost = () => {
            this.webGLContextLost = true;
            this.webGLContextLostAt = performance.now();
            this.#onContextLost();
        };
        canvas.addEventListener('webglcontextlost', handleContextLost, { once: true });
        canvas.addEventListener('webglcontextrestored', this.#onContextRestored, { once: true });
        this.#removeContextListeners = () => {
            canvas.removeEventListener('webglcontextlost', handleContextLost);
            canvas.removeEventListener('webglcontextrestored', this.#onContextRestored);
        };
    }

    #captureWebglContextInfo() {
        if (!this.renderer) {
            return;
        }
        const gl = this.renderer.gl;
        const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
        if (!debugInfo) {
            return;
        }
        this.webglContextInfo.renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
        this.webglContextInfo.vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
        this.webglContextInfo.extensions = (gl.getSupportedExtensions() ?? []).join(', ');
    }

    growIfNeeded(outputW: number, outputH: number) {
        if (!this.renderer) {
            return;
        }
        const { maxOutput } = LuminaryRendererManager.#shared;
        maxOutput.width = Math.max(maxOutput.width, outputW);
        maxOutput.height = Math.max(maxOutput.height, outputH);
        this.#fitCanvasToOutputs();
    }

    async destroy() {
        if (!this.renderer) {
            return;
        }
        this.#removeContextListeners?.();
        this.#removeContextListeners = null;
        this.renderer = null;

        const shared = LuminaryRendererManager.#shared;
        shared.count -= 1;
        if (shared.count > 0) {
            return;
        }
        if (!shared.ready && shared.initPromise !== null) {
            const pendingInit = shared.initPromise;
            try {
                await pendingInit;
            } catch {}
            if (shared.count > 0 || shared.initPromise !== pendingInit) {
                return;
            }
        }
        try {
            shared.current?.destroy(true);
        } catch (err) {
            console.warn('WebGLRenderer destroy failed:', err);
        }
        LuminaryRendererManager.#resetShared();
    }
}
